/**
 * Verify Credential Modal
 * Modal for updating credential verification status
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  Box,
  Typography,
} from '@mui/material'
import { useForm } from '@tanstack/react-form'
import { z } from 'zod'
import { Check as SubmitIcon, Cancel as CancelIcon } from '@mui/icons-material'
import axios from 'src/utilities/http-client'
import {
  getZodValidationError,
  shouldShowZodFieldError,
} from 'src/v3/utilities/validation'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchCredentialVerificationStatuses,
  selectActiveLinkedService,
} from 'src/v3/manage/manage.slice'
import type { RosterCredentialItem } from '../../types'

interface VerifyCredentialModalProps {
  isOpen: boolean
  credential: RosterCredentialItem | null
  userID: string
  onClose: () => void
  onSuccess: () => void
  onFailure: (error: string) => void
}

let verificationFormSchema = z
  .object({
    verificationStatus: z.string().nonempty('Verification status is required'),
    rejectionReason: z.string(),
  })
  .refine(
    (data) => {
      if (data.verificationStatus === 'rejected') {
        return data.rejectionReason.trim().length > 0
      }
      return true
    },
    {
      message: 'Rejection reason is required',
      path: ['rejectionReason'],
    },
  )

export let VerifyCredentialModal = ({
  isOpen,
  credential,
  onClose,
  onSuccess,
  onFailure,
}: VerifyCredentialModalProps) => {
  let dispatch = useAppDispatch()

  let linkedServiceID = useAppSelector(selectActiveLinkedService)

  let form = useForm({
    defaultValues: {
      verificationStatus: 'accepted',
      rejectionReason: '',
    },
    validators: {
      onMount: verificationFormSchema,
      onChange: verificationFormSchema,
    },
    onSubmit: async ({ value }) => {
      if (!credential?.id) return

      try {
        await axios
          .post('/api/manage/credential/verify', {
            credentialID: credential.id,
            linkedServiceID,
            status: value.verificationStatus,
            explanation:
              value.verificationStatus === 'rejected'
                ? value.rejectionReason
                : undefined,
          })
          .then(() =>
            dispatch(fetchCredentialVerificationStatuses(linkedServiceID)),
          )

        form.reset()
        onSuccess()
        onClose()
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error updating verification status:', err)
        onFailure('Failed to update verification status')
      }
    },
  })

  let handleClose = () => {
    form.reset()
    onClose()
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="xs">
      <Paper className="px-4 py-2">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
        >
          <DialogTitle className="text-center">Verify Credential</DialogTitle>
          <DialogContent className="flex flex-col gap-4 px-2 pb-6">
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                className="mb-2"
              >
                Credential: <strong>{credential?.type}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Number: <strong>{credential?.number || 'N/A'}</strong>
              </Typography>
            </Box>

            <form.Field name="verificationStatus">
              {(field) => (
                <FormControl error={shouldShowZodFieldError(field.state.meta)}>
                  <FormLabel id="verification-status-label">
                    Verification Decision
                  </FormLabel>
                  <RadioGroup
                    aria-labelledby="verification-status-label"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  >
                    <FormControlLabel
                      value="accepted"
                      control={<Radio />}
                      label="Accept"
                    />
                    <FormControlLabel
                      value="rejected"
                      control={<Radio />}
                      label="Reject"
                    />
                  </RadioGroup>
                  {shouldShowZodFieldError(field.state.meta) && (
                    <Typography
                      variant="caption"
                      color="error"
                      className="mt-1"
                    >
                      {getZodValidationError(field.state.meta.errors)}
                    </Typography>
                  )}
                </FormControl>
              )}
            </form.Field>

            <form.Subscribe
              selector={(state) => [state.values.verificationStatus]}
              children={([status]) =>
                status === 'rejected' && (
                  <form.Field name="rejectionReason">
                    {(field) => (
                      <TextField
                        variant="outlined"
                        size="small"
                        label="Rejection Reason"
                        type="text"
                        fullWidth
                        multiline
                        rows={4}
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        error={shouldShowZodFieldError(field.state.meta)}
                        helperText={
                          shouldShowZodFieldError(field.state.meta) &&
                          getZodValidationError(field.state.meta.errors)
                        }
                      />
                    )}
                  </form.Field>
                )
              }
            />
          </DialogContent>

          <DialogActions
            disableSpacing={true}
            className="flex-col gap-2 pt-0 pb-6 mt-0"
          >
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
              children={([canSubmit, isSubmitting]) =>
                isSubmitting ? (
                  <CircularProgress className="m-auto" size={36} />
                ) : (
                  <>
                    <Button
                      type="submit"
                      color="primary"
                      variant="contained"
                      fullWidth
                      startIcon={<SubmitIcon />}
                      disabled={!canSubmit || isSubmitting}
                    >
                      Submit
                    </Button>
                    <Button
                      onClick={handleClose}
                      color="error"
                      variant="contained"
                      fullWidth
                      startIcon={<CancelIcon />}
                    >
                      Cancel
                    </Button>
                  </>
                )
              }
            />
          </DialogActions>
        </form>
      </Paper>
    </Dialog>
  )
}

export default VerifyCredentialModal
